import { Flex, Tag, Form, Input } from 'antd';
import Avatar from '../../common/components/Avatar';
import EditContainer from './../../common/components/editContainer/EditContainer';
import FormImageUpload from './../../common/components/FormImageUpload/FormImageUpload';
import { useMedia } from '../../hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { constants } from './../../common/constants/constants';
import { fontSize, primitiveColors, spacing } from '../../theming';
import getInputRules, { inputType, validateFullName } from './../../utils/getInputRules';
import PhoneInput from 'react-phone-input-2';
import { getPhoneNumberPlaceholder, getPhoneInputCountry } from '../../utils/phoneInputHelpers';

export default function ProfileDetails({ currentUser, classes, t, handler }) {
    const { i18n } = useTranslation();
    const [form] = Form.useForm();
    const { isSmallDevice, isExtraSmallDevice } = useMedia();
    const { API_BASE_URL } = constants;
    const baseApiUrl = {
        upload: '/users/upload-photo',
        delete: '/users/delete-photo',
        get: '/users/get-photo',
    };

    const inputStyle = {
        borderRadius: 0,
        borderBottom: `1px solid ${primitiveColors.gray100}`,
        padding: 0,
        backgroundColor: 'transparent',
        width: isSmallDevice && '45vw',
    };

    return (
        <Form
            initialValues={{
                fullName: `${currentUser?.firstName} ${currentUser?.lastName}`,
                email: currentUser?.email,
                phoneNumber: currentUser?.phoneNumber,
            }}
            form={form}
            name="edit-profile"
            onFinish={values => {
                handler(values);
            }}
            onChange={() => form.validateFields()}
            style={{ width: 'clamp(250px, 65vw, 500px)' }}
            variant="borderless"
        >
            <Flex vertical={isExtraSmallDevice} gap={!isExtraSmallDevice && spacing[6]}>
                <div style={{ marginTop: spacing[2] }}>
                    <EditContainer
                        children={
                            <Avatar
                                firstName={currentUser?.firstName}
                                lastName={currentUser?.lastName}
                                photoName={currentUser?.photo}
                                controller="users"
                                size={100}
                                style={{
                                    fontSize: fontSize.H1,
                                    borderRadius: spacing[4],
                                    boxShadow: '0px 12px 24px rgba(0, 0, 0, 0.15), 0px 0px 4px rgba(0, 0, 0, 0.12)',
                                    marginBottom: 34,
                                }}
                            />
                        }
                        input={
                            <Form.Item name="photo" onChange={() => form.submit()}>
                                <FormImageUpload
                                    fileQuality={0.7}
                                    fieldName="photo"
                                    path={baseApiUrl}
                                    id={currentUser?.id}
                                    file={
                                        currentUser?.photo && `${API_BASE_URL + baseApiUrl.get}/${currentUser?.photo}`
                                    }
                                    withPreviewModal
                                    setFormFields={value => form.setFieldsValue('photo', value)}
                                    withAddText={false}
                                    containerStyle={{
                                        alignItems: 'center',
                                    }}
                                />
                            </Form.Item>
                        }
                        submit={() => form.submit()}
                        isAvatar
                    />
                </div>
                <Flex vertical gap={spacing[1]}>
                    <EditContainer
                        children={
                            <h1
                                className={classes.title}
                                style={{
                                    fontSize: isSmallDevice && fontSize.H3,
                                    margin: 0,
                                }}
                            >
                                {`${currentUser?.firstName} ${currentUser?.lastName}`}
                            </h1>
                        }
                        input={
                            <Form.Item
                                name="fullName"
                                rules={[
                                    {
                                        validator: validateFullName,
                                    },
                                ]}
                                style={{ marginBottom: 3, width: isSmallDevice && '45vw' }}
                                onBlur={() => form.submit()}
                            >
                                <Input
                                    size="large"
                                    style={{
                                        ...inputStyle,
                                        fontWeight: 500,
                                        fontSize: isSmallDevice ? fontSize.lg : fontSize.H3,
                                    }}
                                />
                            </Form.Item>
                        }
                        submit={() => form.submit()}
                    />
                    <EditContainer
                        children={
                            <h3
                                style={{
                                    margin: `${spacing[0.5]} 0`,
                                    fontSize: isSmallDevice ? fontSize.xs : fontSize.sm,
                                }}
                            >
                                {currentUser?.email}
                            </h3>
                        }
                        input={
                            <Form.Item
                                name="email"
                                rules={getInputRules(t, inputType.EMAIL)}
                                style={{ marginBottom: 0, width: isSmallDevice && '45vw' }}
                                onBlur={() => form.submit()}
                            >
                                <Input
                                    style={{
                                        ...inputStyle,
                                        padding: 2,
                                        fontWeight: 600,
                                        fontSize: isSmallDevice ? fontSize.xs : fontSize.sm,
                                    }}
                                />
                            </Form.Item>
                        }
                        submit={() => form.submit()}
                    />
                    <EditContainer
                        children={
                            <p style={{ margin: `${spacing[1]} 0` }}>{currentUser?.phoneNumber || t('no-phone')}</p>
                        }
                        input={
                            <Form.Item
                                name="phoneNumber"
                                rules={getInputRules(t, inputType.PHONE_NUMBER)}
                                style={{ marginBottom: 0, width: isSmallDevice && '100%' }}
                                onBlur={() => form.submit()}
                            >
                                <PhoneInput
                                    country={getPhoneInputCountry(i18n.language)}
                                    placeholder={getPhoneNumberPlaceholder(i18n.language)}
                                    containerClass={classes['phone-input-container']}
                                    inputClass={classes['phone-input']}
                                    buttonClass={classes['phone-input-button']}
                                    onChange={(value, country, e, formattedValue) => {
                                        form.setFieldsValue({ phoneNumber: formattedValue });
                                    }}
                                />
                            </Form.Item>
                        }
                        submit={() => form.submit()}
                    />
                </Flex>
            </Flex>
        </Form>
    );
}
